import { ChangeEvent, KeyboardEvent, KeyboardEventHandler } from "react";

/**
 * Prevents decimal point, minus, plus, and exponent inputs in number fields
 * This is commonly used in forms where only whole numbers are allowed
 *
 * @param e - Keyboard event from input field
 */
const preventDecimalAndSignInputs = (
  e: React.KeyboardEvent<HTMLInputElement>
): void => {
  if (
    e.key === "." ||
    e.key === "-" ||
    e.key === "+" ||
    e.key === "e" ||
    e.key === "E"
  ) {
    e.preventDefault();
  }
};

/**
 * Prevents minus, plus, and exponent inputs in number fields
 * This is commonly used in forms where decimal numbers are allowed but signs and exponents are not
 *
 * @param e - Keyboard event from input field
 */
const preventSignInputs = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
    e.preventDefault();
  }
};

/**
 * Handler function that can be directly used as onKeyDown prop
 * Prevents decimal point, minus, plus, and exponent inputs in number fields
 * This is commonly used in forms where only whole numbers are allowed
 *
 * @param e - Keyboard event from input field
 */
export const handleNumberInputKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
): void => {
  preventDecimalAndSignInputs(e);
};

/**
 * Handler function for decimal number inputs
 * Prevents minus, plus, and exponent inputs but allows decimal point
 * This is commonly used in forms where decimal numbers are allowed
 *
 * @param e - Keyboard event from input field
 */
export const handleDecimalInputKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
): void => {
  preventSignInputs(e);
};

/**
 * Removes leading spaces from input value
 * This is commonly used in form fields to prevent users from starting input with spaces
 *
 * @param value - Input string value
 * @returns Input value with leading spaces removed
 */
export const trimLeadingSpaces = (value: string): string => {
  // Remove all leading whitespace if present, otherwise return original value
  const trimmed = value.trimStart();
  return trimmed !== value ? trimmed : value;
};

/**
 * Handles alphanumeric-only input for text fields
 * Filters out any special characters but allows letters and numbers
 * This is commonly used in fields like zip codes, postal codes, or alphanumeric IDs
 *
 * @param event - The input change event
 * @param callback - Callback function to update the field value
 * @param maxLength - Optional maximum length for the input
 *
 * @example
 * ```tsx
 * <TextField
 *   {...field}
 *   onChange={(e) => handleAlphanumericInput(e, field.onChange, 10)}
 * />
 * ```
 */
export const handleAlphanumericInput = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  callback: (value: string) => void,
  maxLength?: number
): void => {
  // Allow only letters (a-z, A-Z) and numbers (0-9)
  let value = event.target.value.replace(/[^a-zA-Z0-9]/g, "");

  if (maxLength && value.length > maxLength) {
    value = value.slice(0, maxLength);
  }

  callback(value);
};

/**
 * Handles numeric-only input for text fields
 * Filters out any non-numeric characters
 * This is commonly used in text fields where only numbers should be allowed
 *
 * @param event - The input change event
 * @param callback - Callback function to update the field value
 * @param maxLength - Optional maximum length for the input
 *
 * @example
 * ```tsx
 * <TextField
 *   {...field}
 *   onChange={(e) => handleNumericInput(e, field.onChange, 5)}
 * />
 * ```
 */
export const handleNumericInput = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  callback: (value: string) => void,
  maxLength?: number
): void => {
  let value = event.target.value.replace(/[^0-9]/g, "");

  if (maxLength && value.length > maxLength) {
    value = value.slice(0, maxLength);
  }

  callback(value);
};

/**
 * Creates an isAllowed function for NumericFormat that limits the number of digits
 * This is commonly used with react-number-format's NumericFormat component
 *
 * @param maxDigits - Maximum number of digits allowed (excluding decimal point)
 * @returns A function that can be passed to NumericFormat's isAllowed prop
 *
 * @example
 * ```tsx
 * <NumericFormat
 *   isAllowed={createMaxDigitsAllowed(10)}
 *   // ... other props
 * />
 * ```
 */
export const createMaxDigitsAllowed =
  (maxDigits: number) =>
  (values: { value: string }): boolean => {
    const { value } = values;
    return value.replace(".", "").length <= maxDigits;
  };

/**
 * Prevents space key when input has no value.
 * Used in Autocomplete/search inputs to avoid accidental selection or trigger when the field is empty.
 *
 * @param e - Keyboard event from input field
 * @param originalOnKeyDown - Optional original onKeyDown handler to call after
 */
export const preventSpaceWhenEmptyKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
  originalOnKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
): void => {
  if (e.key === " " && !e.currentTarget.value) {
    e.preventDefault();
  }
  originalOnKeyDown?.(e);
};

type AutocompleteRecipientFieldKeyboardEvent = KeyboardEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

type HandleAutocompleteEnterCommitKeyDownParams = {
  event: AutocompleteRecipientFieldKeyboardEvent;
  upstreamOnKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  isListboxOpen: boolean;
  getDraftTrimmed: () => string;
  onCommit: () => void;
};

export const handleAutocompleteEnterCommitKeyDown = ({
  event,
  upstreamOnKeyDown,
  isListboxOpen,
  getDraftTrimmed,
  onCommit,
}: HandleAutocompleteEnterCommitKeyDownParams): void => {
  const draftTrimmed = getDraftTrimmed();
  if (
    draftTrimmed.length === 0 &&
    (event.key === "Backspace" || event.key === "Delete")
  ) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const forwardToUpstream = (): void => {
    upstreamOnKeyDown?.(event as KeyboardEvent<HTMLInputElement>);
  };
  if (event.key !== "Enter") {
    forwardToUpstream();
    return;
  }
  if (isListboxOpen) {
    forwardToUpstream();
    return;
  }
  if (draftTrimmed.length > 0) {
    event.preventDefault();
    event.stopPropagation();
    onCommit();
    return;
  }
  event.preventDefault();
  event.stopPropagation();
};

type HandleEnterEscapeKeyDownParams = {
  event: AutocompleteRecipientFieldKeyboardEvent | KeyboardEvent<HTMLElement>;
  onEnter?: () => void;
  onEscape?: () => void;
  upstreamOnKeyDown?: (
    event: AutocompleteRecipientFieldKeyboardEvent | KeyboardEvent<HTMLElement>
  ) => void;
};

const handleEnterEscapeKeyDown = ({
  event,
  onEnter,
  onEscape,
  upstreamOnKeyDown,
}: HandleEnterEscapeKeyDownParams): void => {
  if (event.key === "Enter") {
    event.preventDefault();
    onEnter?.();
    return;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    onEscape?.();
    return;
  }
  upstreamOnKeyDown?.(event);
};

type HandleRecipientChipEditKeyDownParams = {
  event: AutocompleteRecipientFieldKeyboardEvent | KeyboardEvent<HTMLElement>;
  onEnterCommit: () => void;
  onEscapeCancel: () => void;
  upstreamOnKeyDown?: (
    event: AutocompleteRecipientFieldKeyboardEvent | KeyboardEvent<HTMLElement>
  ) => void;
};

const CHIP_EDIT_PASSTHROUGH_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Backspace",
  "Delete",
]);

export const handleRecipientChipEditKeyDown = (
  params: HandleRecipientChipEditKeyDownParams
): void => {
  const { event, onEnterCommit, onEscapeCancel, upstreamOnKeyDown } = params;
  if (CHIP_EDIT_PASSTHROUGH_KEYS.has(event.key)) {
    event.stopPropagation();
  }
  handleEnterEscapeKeyDown({
    event,
    onEnter: onEnterCommit,
    onEscape: onEscapeCancel,
    upstreamOnKeyDown,
  });
};

/**
 * Handles keyboard toggle for Switch components
 * Enables Enter and Space keys to toggle the switch state
 * This is commonly used with MUI Switch inside FormControlLabel
 *
 * @param e - Keyboard event from Switch component
 * @param currentValue - Current boolean value of the switch
 * @param onChange - Callback function to update the switch value
 *
 * @example
 * ```tsx
 * <Switch
 *   checked={field.value}
 *   onChange={field.onChange}
 *   onKeyDown={(e) => handleSwitchKeyDown(e, field.value, field.onChange)}
 * />
 * ```
 */
export const handleSwitchKeyDown = (
  e: React.KeyboardEvent,
  currentValue: boolean,
  onChange: (value: boolean) => void
): void => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onChange(!currentValue);
  }
};

export const handleTabToAddField = (
  event: KeyboardEvent<HTMLInputElement>,
  index: number,
  fieldCount: number,
  onAddField: () => void
): void => {
  if (event.key !== "Tab" || event.shiftKey || index !== fieldCount - 1) {
    return;
  }
  event.preventDefault();
  onAddField();
};
